import { Logger } from '~~/helpers/logger';
import type { Category } from '~~/types/commercetools';
import { CUSTOM_QUERIES } from '~~/constants/customQueries';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { ALLOWED_METHODS } from '~~/types/expondoMiddleware';
import { removeEmptyCategories, addEmptyChildren } from '~~/helpers/category/categoriesTree';
import { extractAncestorsFromCategory } from '~~/helpers/category/extractAncestorsFromCategory';
import { addCategoryToCategoryAncestors } from '~~/helpers/category/addCategoryToCategoryAncestors';
import { buildTreeFromArray } from '~~/helpers/category/buildTreeFromArray';
import { sortCategories } from '~~/helpers/category/sortCategories';
import { errorToString } from '~~/helpers/error/errorToString';

type FacetResultsDataExtended = {
  data: {
    results: [];
    categories: Category[];
    facets: [];
    rootCategory?: Category | null;
    currentCategory?: Category | null;
    activeCategories: Category[];
    total: number;
    attributeDefinitions: [];
  }
};

export function useFacet(id: string) {

  const facets = useState<FacetResultsDataExtended | null>(`useFacetData-${id}`, () => null);
  const loading = useState(`useFacetLoading-${id}`, () => false);
  const error = useState<{ search: null | string }>(`useFacetError-${id}`, () => ({ search: null }));
  const apiFetch = useApiFetch();

  async function search(params: any) {

    Logger.debug(`useFacet/${id}/search`, params);

    try {

      loading.value = true;
      const acceptLanguage = params.languageAndCountry;
      const endpoint = `${EXPONDO_MIDDLEWARE_ROUTES.CT.ROOT}${EXPONDO_MIDDLEWARE_ROUTES.CT.GET_CATEGORY}`;
      const fullPath = `${EXPONDO_MIDDLEWARE_ROUTES.ROOT}${endpoint}`;
      const { data: categoryResponse } =
        await apiFetch(fullPath,{
          method: ALLOWED_METHODS.post,
          body: [
            {
              slug: params?.categorySlug,
              acceptLanguage,
              fetchSiblings: params?.fetchSiblings,
            },
            {
              categories: CUSTOM_QUERIES.CATEGORIES.LISTING,
            },
          ],
          credentials: 'same-origin',
          headers: {
            ...params?.headers,
            'Content-Type': 'application/json',
          },
        });
      const categoryRawResults: Category[] = categoryResponse?.categories?.results || [];
      const currentCategoryResults = removeEmptyCategories(categoryRawResults, acceptLanguage);
      const currentCategory = currentCategoryResults[0];

      sortCategories(currentCategory?.children);
      const ancestors = extractAncestorsFromCategory(currentCategory);
      addCategoryToCategoryAncestors(ancestors, currentCategory);

      const categories: Category[] = addEmptyChildren(buildTreeFromArray(ancestors));

      if (categories[0]) {

        categories[0].parent = null;
      
      }
      facets.value = {
        data: {
          results: [],
          categories,
          facets: [],
          rootCategory: categories[0] || null,
          currentCategory,
          activeCategories: ancestors,
          total: 0,
          attributeDefinitions: [],
        },
      };
      error.value.search = null;
    
    } catch (err: unknown) {

      error.value.search = errorToString(err);
      Logger.error(`useFacet/${id}/search`, err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  return {
    search,
    facets: computed(() => facets.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };

}
